import type { CSSProperties } from "react";
import type { HeroScene } from "@/content/home-hero";

/**
 * Cenas ilustrativas da hero com slider. SVG/HTML puro, sem dados reais
 * (nada de número inventado): servem para passar movimento e "poder
 * tecnológico". Cores vêm dos tokens do tema, então funcionam no escuro e no claro.
 * As animações só rodam no slide ativo (`.hx-scene.is-active …` em app.css) e
 * ficam paradas com prefers-reduced-motion.
 */

/* ------------------------------------------------------------------ ERP hub */
const MODULES = [
  { label: "Fiscal", x: 260, y: 52 },
  { label: "Estoque", x: 436, y: 150 },
  { label: "Financeiro", x: 436, y: 310 },
  { label: "Faturamento", x: 260, y: 408 },
  { label: "Compras", x: 84, y: 310 },
  { label: "Contábil", x: 84, y: 150 },
];

function ErpScene() {
  const cx = 260;
  const cy = 230;
  return (
    <svg className="hx-svg" viewBox="0 0 520 460" role="img" aria-label="Ilustração: módulos do Protheus integrados">
      <defs>
        <radialGradient id="hxCore" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="var(--c-brand-cyan)" />
          <stop offset="100%" stopColor="var(--c-brand-deep)" />
        </radialGradient>
        <linearGradient id="hxRing" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--c-brand-cyan)" />
          <stop offset="100%" stopColor="var(--c-brand-violet)" />
        </linearGradient>
      </defs>

      <circle className="hx-orbit" cx={cx} cy={cy} r="128" />
      <circle className="hx-orbit hx-orbit--slow" cx={cx} cy={cy} r="186" />

      {MODULES.map((m, i) => {
        const d = `M${cx} ${cy} L${m.x} ${m.y}`;
        return (
          <g key={m.label}>
            <path className="hx-link" d={d} style={{ animationDelay: `${i * 0.18}s` }} />
            <circle className="hx-packet" r="4">
              <animateMotion dur={`${2.4 + i * 0.3}s`} repeatCount="indefinite" path={d} begin={`${i * 0.4}s`} />
            </circle>
          </g>
        );
      })}

      <g className="hx-core">
        <circle cx={cx} cy={cy} r="70" fill="none" stroke="url(#hxRing)" strokeWidth="2" className="hx-core__ring" />
        <circle cx={cx} cy={cy} r="54" fill="url(#hxCore)" />
        <text x={cx} y={cy - 4} textAnchor="middle" className="hx-core__label">PROTHEUS</text>
        <text x={cx} y={cy + 16} textAnchor="middle" className="hx-core__sub">ERP integrado</text>
      </g>

      {MODULES.map((m, i) => (
        <g key={m.label} className="hx-node" style={{ animationDelay: `${0.2 + i * 0.12}s` }}>
          <rect x={m.x - 56} y={m.y - 19} width="112" height="38" rx="19" className="hx-node__pill" />
          <circle cx={m.x - 36} cy={m.y} r="5" className="hx-node__dot" />
          <text x={m.x - 24} y={m.y + 5} className="hx-node__label">{m.label}</text>
        </g>
      ))}
    </svg>
  );
}

/* ---------------------------------------------------------------- Power BI */
const BARS = [0.42, 0.58, 0.5, 0.72, 0.66, 0.88];

function BiScene() {
  const baseY = 300;
  const maxH = 150;
  return (
    <svg className="hx-svg" viewBox="0 0 520 460" role="img" aria-label="Ilustração: painel de indicadores em Power BI">
      <defs>
        <linearGradient id="hxBar" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--c-brand-cyan)" />
          <stop offset="100%" stopColor="var(--c-brand)" stopOpacity="0.35" />
        </linearGradient>
        <linearGradient id="hxArea" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--c-brand-violet)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--c-brand-violet)" stopOpacity="0" />
        </linearGradient>
      </defs>

      <rect x="30" y="40" width="460" height="380" rx="22" className="hx-panel" />
      <circle cx="58" cy="68" r="5" className="hx-panel__dot" />
      <circle cx="76" cy="68" r="5" className="hx-panel__dot" />
      <circle cx="94" cy="68" r="5" className="hx-panel__dot" />
      <text x="118" y="73" className="hx-panel__title">painel-diretoria.pbix</text>

      {/* KPI tiles (sem valores: ilustração) */}
      {["Vendas", "Estoque", "Margem"].map((k, i) => (
        <g key={k} className="hx-kpi" style={{ animationDelay: `${0.15 + i * 0.15}s` }}>
          <rect x={56 + i * 140} y="96" width="124" height="62" rx="12" className="hx-kpi__box" />
          <text x={72 + i * 140} y="121" className="hx-kpi__label">{k}</text>
          <path d={`M${72 + i * 140} 143 l14 -8 l12 4 l16 -12 l14 6 l16 -10`} className="hx-kpi__spark" />
          <path d={`M${160 + i * 140} 128 l6 -8 l6 8`} className="hx-kpi__up" />
        </g>
      ))}

      {/* grid */}
      {[0, 1, 2, 3].map((i) => (
        <line key={i} x1="56" x2="320" y1={baseY - i * 50} y2={baseY - i * 50} className="hx-gridline" />
      ))}

      {/* bars */}
      {BARS.map((h, i) => (
        <rect
          key={i}
          className="hx-bar"
          x={66 + i * 42}
          y={baseY - h * maxH}
          width="26"
          height={h * maxH}
          rx="6"
          fill="url(#hxBar)"
          style={{ animationDelay: `${0.3 + i * 0.1}s` }}
        />
      ))}

      {/* trend line */}
      <path className="hx-area" d="M66 270 L108 252 L150 260 L192 222 L234 230 L276 190 L310 178 L310 300 L66 300 Z" fill="url(#hxArea)" />
      <path className="hx-trend" d="M66 270 L108 252 L150 260 L192 222 L234 230 L276 190 L310 178" />

      {/* donut */}
      <g transform="translate(406 228)">
        <circle r="58" className="hx-donut__track" />
        <circle r="58" className="hx-donut hx-donut--a" pathLength="100" />
        <circle r="58" className="hx-donut hx-donut--b" pathLength="100" />
        <circle r="40" className="hx-donut__hole" />
        <text y="5" textAnchor="middle" className="hx-donut__label">mix</text>
      </g>

      <text x="56" y="340" className="hx-axis">jan</text>
      <text x="290" y="340" className="hx-axis">dez</text>
      <text x="360" y="320" className="hx-legend">● produtos  ● serviços</text>
      <text x="56" y="392" className="hx-caption">Atualizado direto do Protheus</text>
      <circle cx="268" cy="388" r="5" className="hx-live" />
    </svg>
  );
}

/* ------------------------------------------------------------------- ADVPL */
type Tok = [string, string?];
const CODE: Tok[][] = [
  [["#Include", "kw"], [' "Protheus.ch"', "str"]],
  [],
  [["User Function", "kw"], [" A010TOK", "fn"], ["()"]],
  [["    Local", "kw"], [" lRet := "], [".T.", "lit"]],
  [],
  [["    // valida o cadastro antes de gravar", "cm"]],
  [["    If", "kw"], [" Empty("], ["M->B1_POSIPI", "var"], [")"]],
  [["        MsgAlert", "fn"], ["("], ['"Informe o NCM do produto."', "str"], [")"]],
  [["        lRet := "], [".F.", "lit"]],
  [["    EndIf", "kw"]],
  [["Return", "kw"], [" lRet"]],
];

function CodeScene() {
  return (
    <div className="hx-code" role="img" aria-label="Ilustração: ponto de entrada em ADVPL">
      <div className="hx-code__bar">
        <span className="hx-code__dots" aria-hidden>
          <i />
          <i />
          <i />
        </span>
        <span className="hx-code__file">A010TOK.prw</span>
        <span className="hx-code__tag">ADVPL</span>
      </div>
      <pre className="hx-code__body">
        {CODE.map((line, i) => {
          const text = line.map((t) => t[0]).join("");
          return (
            <span
              key={i}
              className="hx-code__line"
              style={{ ["--i" as string]: i, ["--n" as string]: Math.max(text.length, 1) } as CSSProperties}
            >
              <span className="hx-code__ln" aria-hidden>{String(i + 1).padStart(2, "0")}</span>
              <span className="hx-code__txt">
                {line.length === 0 ? " " : line.map((t, j) => (t[1] ? <span key={j} className={`tk-${t[1]}`}>{t[0]}</span> : <span key={j}>{t[0]}</span>))}
              </span>
            </span>
          );
        })}
      </pre>
      <div className="hx-code__status">
        <span className="hx-code__ok" aria-hidden>✓</span> Compilado · pronto para homologação
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- chips flutuantes */
const CHIPS: Record<HeroScene, { text: string; pos: string }[]> = {
  erp: [
    { text: "SPED validado", pos: "hx-float--tl" },
    { text: "Estoque conciliado", pos: "hx-float--br" },
  ],
  bi: [
    { text: "Painel ao vivo", pos: "hx-float--tr" },
    { text: "Sem planilha", pos: "hx-float--bl" },
  ],
  code: [
    { text: "Ponto de entrada", pos: "hx-float--tr" },
    { text: "Homologado", pos: "hx-float--bl" },
  ],
};

export function HeroSceneArt({ scene }: { scene: HeroScene }) {
  return (
    <>
      {scene === "erp" ? <ErpScene /> : scene === "bi" ? <BiScene /> : <CodeScene />}
      {CHIPS[scene].map((c, i) => (
        <span key={c.text} className={`hx-float ${c.pos}`} style={{ animationDelay: `${i * 1.4}s` }} aria-hidden>
          <span className="hx-float__dot" />
          {c.text}
        </span>
      ))}
    </>
  );
}

/* ---------------------------------------------------------------- ícones das abas */
export function HeroTabIcon({ scene }: { scene: HeroScene }) {
  if (scene === "erp") {
    return (
      <svg className="hx-ico hx-ico--erp" viewBox="0 0 32 32" aria-hidden>
        <path d="M16 3 27 9.5v13L16 29 5 22.5v-13Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M16 3v26M5 9.5l22 13M27 9.5 5 22.5" stroke="currentColor" strokeWidth="1" opacity=".35" />
        <circle cx="16" cy="16" r="3.6" fill="currentColor" className="hx-ico__core" />
      </svg>
    );
  }
  if (scene === "bi") {
    return (
      <svg className="hx-ico hx-ico--bi" viewBox="0 0 32 32" aria-hidden>
        <rect x="5" y="17" width="5" height="10" rx="1.5" fill="currentColor" className="hx-ico__b1" />
        <rect x="13.5" y="11" width="5" height="16" rx="1.5" fill="currentColor" className="hx-ico__b2" />
        <rect x="22" y="6" width="5" height="21" rx="1.5" fill="currentColor" className="hx-ico__b3" />
      </svg>
    );
  }
  return (
    <svg className="hx-ico hx-ico--code" viewBox="0 0 32 32" aria-hidden>
      <path d="M11 9 4 16l7 7M21 9l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18 7 14 25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="hx-ico__slash" />
    </svg>
  );
}
